import merge from 'lodash/merge';
import Headroom from 'headroom.js';

/**
 * SlideupHandler
 *
 * - SlideupHandler doesn’t rely on a mininum or maximum width. Instead, it relies on the CSS cursor
 *   property of the toggle button. This technique is inspired by
 *   https://www.filamentgroup.com/lab/accessible-responsive.html.
 * - The handling of the ARIA states of the toggle button and the menu are inspired by the book
 *   "Inclusive Design Patterns" by Heydon Pickering, p. 172 ff.
 */
export function slideup(options) {
  var defaults = {
    openOnStartup: false,
    selectors: {
      panel: '#page',
      menu: '#navbar',
      open: '.js-menu-open',
      close: '.js-menu-close',
      currentMenuParentLink: '.js-current-menu-parent-link'
    },
    classes: {
      isOpen: 'slideup-open',
      isOpening: 'is-opening',
      isClosing: 'is-closing',
      toggleOpen: 'is-open'
    },
    headroom: {
      mobile: {
        enabled: true,
        $el: document.getElementById('nav-mobile'),
        options: {
          // Vertical offset in px before element is first unpinned
          offset: 55
        }
      },
      desktop: {
        // Headroom is disabled on desktop per default
        enabled: false,
        $el: document.getElementById('navbar'),
        options: {
          // Vertical offset in px before element is first unpinned
          offset: 85
        }
      }
    }
  };

  function SlideupHandler(options) {
    // Extend options (respecting deeply-nested objects)
    this.options = merge({}, defaults, options);

    this.isBound = false;
    this.isOpen = false;
    this.isAnimating = false;

    this.el = {
      $open: document.querySelector(this.options.selectors.open),
      $close: document.querySelector(this.options.selectors.close),
      $menu: document.querySelector(this.options.selectors.menu),
      $currentMenuParentLink: document.querySelector(
        this.options.selectors.currentMenuParentLink
      )
    };

    this.eventTypes = {
      transitionEnd: [
        'transitionend',
        'webkitTransitionEnd',
        'oTransitionEnd',
        'MSTransitionEnd'
      ]
    };

    this.listeners = {
      open: this.onClickOpen.bind(this),
      close: this.onClickClose.bind(this),
      handleTransitionComplete: this.handleTransitionComplete.bind(this),
      transitionCompleteClose: this.transitionCompleteClose.bind(this)
    };

    /**
     * Storage for single headroom objects
     * @type object
     */
    this.headrooms = {};
  }

  SlideupHandler.prototype = {
    init: function() {
      var resizeTimer;

      window.addEventListener(
        'resize',
        function() {
          clearTimeout(resizeTimer);
          resizeTimer = setTimeout(this.resizeHandler.bind(this), 50);
        }.bind(this)
      );

      setTimeout(this.resizeHandler.bind(this), 0);

      if (this.options.openOnStartup) {
        this.el.$open.click();
      }
    },

    /**
     * Bind or unbind mobile menu based on the cursor CSS property of the mobile toggle button.
     *
     * If the button has a cursor property != default, then the mobile menu will be bound.
     */
    resizeHandler: function() {
      if (getComputedStyle(this.el.$open)['cursor'] === 'pointer') {
        if (!this.isBound) {
          this.bind();
        }
      } else {
        if (this.isBound) {
          this.destroy();
        } else {
          this.applyHeadroom('desktop');
        }
      }
    },

    bind: function() {
      this.el.$open.addEventListener('click', this.listeners.open);
      this.el.$close.addEventListener('click', this.listeners.close);

      // Add support for Turbolinks
      document.addEventListener('turbolinks:visit', this.listeners.close);
      document.addEventListener(
        'turbolinks:visit',
        this.listeners.transitionCompleteClose
      );

      // Add support for smoothScroll plugin
      document.addEventListener('smooth-scroll', this.listeners.close);

      this.applyHeadroom('mobile');

      // Set menu state to hidden. This makes all child elements unfocusable.
      this.el.$menu.hidden = true;

      // Open the current menu item when it’s collapsed.
      if (
        this.el.$currentMenuParentLink &&
        this.el.$currentMenuParentLink.getAttribute('aria-expanded') === 'false'
      ) {
        this.el.$currentMenuParentLink.click();
      }

      this.isBound = true;
    },

    unbind: function() {
      this.el.$open.removeEventListener('click', this.listeners.open);
      this.el.$close.removeEventListener('click', this.listeners.close);

      this.removeAnimationListeners();

      document.removeEventListener('turbolinks:visit', this.listeners.close);
      document.removeEventListener(
        'turbolinks:visit',
        this.listeners.transitionCompleteClose
      );

      document.removeEventListener('smooth-scroll', this.listeners.close);

      this.applyHeadroom('desktop');

      // Remove ARIA roles
      this.el.$menu.removeAttribute('hidden');
    },

    addAnimationListeners: function() {
      // Add event listeners for different browser transition names.
      this.eventTypes.transitionEnd.forEach(
        function(transition) {
          this.el.$menu.addEventListener(
            transition,
            this.listeners.handleTransitionComplete
          );
        }.bind(this)
      );
    },

    removeAnimationListeners: function() {
      // Remove event listeners for different browser transition names.
      this.eventTypes.transitionEnd.forEach(
        function(transition) {
          this.el.$menu.removeEventListener(
            transition,
            this.listeners.handleTransitionComplete
          );
        }.bind(this)
      );
    },

    /**
     * Apply correct Headroom for mobile and desktop menu
     *
     * Desktop headroom should not be applied on mobile, because when the content is scrolled,
     * the mobile menu content would transform away.
     */
    applyHeadroom: function(which) {
      this.checkHeadroom('desktop', which);
      this.checkHeadroom('mobile', which);
    },

    checkHeadroom: function(key, which) {
      var options = this.options.headroom[key];

      // Bail out if element does not exist
      if (
        options.enabled === false ||
        !options.$el ||
        options.$el.length === 0
      ) {
        return true;
      }

      if (key === which) {
        // Apply
        if (typeof this.headrooms.which === 'undefined') {
          this.headrooms[which] = new Headroom(options.$el, options.options);
          this.headrooms[which].init();
        }
      } else {
        // Check if applied and unapply
        if (typeof this.headrooms.which !== 'undefined') {
          this.headrooms.which.destroy();
        }
      }
    },

    /**
     * Check if Headroom is applied for mobile menu and destroy if it is.
     */
    onBeforeOpen: function(/* evt */) {
      if (typeof this.headrooms.mobile !== 'undefined') {
        this.headrooms.mobile.destroy();
      }
    },

    /**
     * Apply Headroom to mobile menu
     */
    onBeforeClose: function() {
      this.applyHeadroom('mobile');
    },

    handleTransitionComplete: function(evt) {
      // Bailout if wrong target
      if (evt.target !== evt.currentTarget) {
        return true;
      }

      this.removeAnimationListeners();

      if (!this.isOpen) {
        this.transitionCompleteOpen();
      } else {
        this.transitionCompleteClose();
      }

      this.isAnimating = false;
    },

    transitionCompleteOpen: function() {
      this.isOpen = true;

      document.documentElement.classList.remove(this.options.classes.isOpening);
    },

    transitionCompleteClose: function() {
      this.isOpen = false;

      document.documentElement.classList.remove(
        this.options.classes.isClosing,
        this.options.classes.isOpen
      );
    },

    onClickOpen: function() {
      // Bailout if already animating or open.
      if (this.isAnimating || this.isOpen) {
        return true;
      }

      this.addAnimationListeners();
      this.isAnimating = true;
      this.open();
    },

    onClickClose: function() {
      // Bailout if already animating or open.
      if (this.isAnimating || !this.isOpen) {
        return true;
      }

      this.addAnimationListeners();
      this.isAnimating = true;
      this.close();
    },

    open: function() {
      document.documentElement.classList.add(this.options.classes.isOpen);
      document.documentElement.classList.add(this.options.classes.isOpening);

      this.setAriaStates(false);
    },

    close: function() {
      document.documentElement.classList.add(this.options.classes.isClosing);

      this.setAriaStates(true);
    },

    setAriaStates: function(expanded) {
      this.el.$menu.hidden = expanded;
    },

    destroy: function() {
      this.unbind();
      this.isBound = false;
    }
  };

  /**
   * Make sure SlideupHandler always fires.
   *
   * The DOMContentLoaded event could already have run. This is especially true
   * if we load polyfills first. If the event already ran, we directly
   * initialize the SlideupHandler.
   *
   * @link https://stackoverflow.com/questions/24904010/javascript-domcontentloaded-event-not-firing-in-internet-explorer
   */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      new SlideupHandler(options).init();
    });
  } else {
    new SlideupHandler(options).init();
  }
}
